import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { IJobPostDialogProps } from '../../interfaces';

const SEVERITY_BORDER_COLOR: Record<NonNullable<IJobPostDialogProps['severity']>, string> = {
  info: 'info.main',
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
};

const JUSTIFY_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
} as const;

/**
 * Brand-styled dialog replacing the legacy custom `Dialog` for the public job
 * portal. Colored top border mirrors the old `dialogType` accent.
 */
export const JobPostDialog = ({
  isOpen,
  onClose,
  title,
  children,
  onConfirm,
  confirmLabel,
  cancelLabel,
  confirmDisabled = false,
  maxWidth = 'sm',
  disableOutsideClose = false,
  actionsAlign = 'end',
  severity = 'info',
}: IJobPostDialogProps) => (
  <Dialog
      open={isOpen}
      onClose={(_e, reason) => {
        if (disableOutsideClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        onClose();
      }}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby="job-post-dialog-title"
      PaperProps={{
        sx: { borderTop: 4, borderColor: SEVERITY_BORDER_COLOR[severity] },
      }}
  >
    <DialogTitle id="job-post-dialog-title">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="h3">{title}</Typography>
        <IconButton aria-label="Close dialog" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </DialogTitle>
    <DialogContent dividers={false}>{children}</DialogContent>
    <DialogActions sx={{ justifyContent: JUSTIFY_MAP[actionsAlign], px: 3, pb: 2.5 }}>
      {cancelLabel && (
        <Button variant="outlined" onClick={onClose}>
          {cancelLabel}
        </Button>
      )}
      <Button variant="contained" onClick={onConfirm} disabled={confirmDisabled}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);
